import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { Spinner } from "@/components/ui/Spinner";
import { BillingAnswersPreviewDialog } from "@/features/timesheetFlow/components/BillingAnswersPreviewDialog";
import { useCurrentUser } from "@/lib/auth";
import { fetchTimesheetsWithAnsweredBillingQuestions } from "@/lib/home";
import type { TimesheetSummary } from "@/lib/timesheets";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  getTimesheetDisplayNumber,
  getTimesheetStatus,
  statusBadgeClass,
  timesheetStatusLabel,
} from "@/pages/stundennachweise/timesheetFilterLogic";

function formatAnsweredAt(value?: string | null): string {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Modal: Stundennachweise mit vollständig beantworteten Rückfragen. */
export function AnsweredBillingTimesheetsDialog({
  open,
  onClose,
  onOpenTimesheet,
  onOpenProjectOverview,
}: {
  open: boolean;
  onClose: () => void;
  onOpenTimesheet: (timesheetId: number) => void;
  onOpenProjectOverview: (auftragId: number) => void;
}) {
  const { user } = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<TimesheetSummary[]>([]);
  const [answersTimesheet, setAnswersTimesheet] = useState<TimesheetSummary | null>(null);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setError(null);
      setLoading(false);
      setAnswersTimesheet(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchTimesheetsWithAnsweredBillingQuestions()
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Beantwortete Rückfragen konnten nicht geladen werden.");
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Beantwortete Rückfragen"
        description="Stundennachweise, bei denen alle Rückfragen beantwortet sind — zuletzt beantwortet zuerst."
        size="2xl"
        fullScreenMobile
        bodyClassName="max-h-[min(70vh,720px)] overflow-y-auto max-sm:max-h-none"
        footer={
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        }
      >
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : error ? (
          <p className="text-sm text-status-red">{error}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-label-secondary">
            Keine vollständig beantworteten Rückfragen vorhanden.
          </p>
        ) : (
          <ul className="space-y-2" role="list">
            {rows.map((row) => {
              const displayNumber = getTimesheetDisplayNumber(row) || "—";
              const projectLabel = [row.auftragsnummer, row.auftrag_display]
                .filter(Boolean)
                .join(" – ");
              const status = getTimesheetStatus(row);
              return (
                <li
                  key={String(row.id ?? displayNumber)}
                  className="rounded-ios border border-separator bg-fill-quaternary/20 px-3 py-2.5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-label">{displayNumber}</p>
                        <TimesheetBadge className={statusBadgeClass(status, user)}>
                          {timesheetStatusLabel(status)}
                        </TimesheetBadge>
                      </div>
                      <p className="mt-1 truncate text-sm text-label">{projectLabel || "—"}</p>
                      <p className="truncate text-xs text-label-secondary">{row.kunde || "—"}</p>
                      <p className="mt-1 text-xs text-label-tertiary">
                        Zuletzt beantwortet: {formatAnsweredAt(row.billing_last_answered_at)}
                      </p>
                    </div>
                    {row.id ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <ProjectOverviewButton
                          auftragId={row.auftrag_id}
                          onOpenOverride={onOpenProjectOverview}
                          className="h-8 w-8 shrink-0"
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          onClick={() => setAnswersTimesheet(row)}
                        >
                          Antworten
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          onClick={() => {
                            onOpenTimesheet(row.id);
                            onClose();
                          }}
                        >
                          Öffnen
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Dialog>

      <BillingAnswersPreviewDialog
        open={answersTimesheet != null}
        onClose={() => setAnswersTimesheet(null)}
        timesheet={answersTimesheet}
      />
    </>
  );
}
